// ساختمان‌ها
import React, { useState, useEffect, useCallback } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { usePagedList, CountBadge } from '../../components/paging';
import { ExcelButton } from '../../components/excel';
import WarningDialog from '../../components/WarningDialog';
import { useFormButtons } from '../../utils/vbPerms';
import {
  API, toFa, curYear, Grid, Field, Lookup, MessageBox, ChoiceBox, ChangeUsesDialog,
  SubFormShell, splitLines, visibleCols, runReport,
} from './BuildingsShared';
import BuildingFacilities from './BuildingFacilities';
import BuildingInsure from './BuildingInsure';
import BuildingRepair from './BuildingRepair';

const FORM = 'FRMBUILDINGS';
const BUTTONS = ['BTNNEW', 'BTNEDIT', 'BTNDELETE', 'BTNOK', 'BTNCANCEL', 'BTNBUILDINGFACILITIES',
  'BTNBUILDINGFACILITIESREQUEST', 'BTNBUILDINGFACILITIESCONFIRM', 'BTNBUILDINGINSURE', 'BTNINSUREREQUEST',
  'BTNINSURECONFIRM', 'BTNBUILDINGREPAIR', 'BTNREPAIRREQUEST', 'BTNREPAIRCONFIRM', 'BTNCHANGE', 'BTNMOVE'];

const COLS = [
  { key: 'creator', label: 'ثبت کننده' },
  { key: 'city', label: 'شهر' },
  { key: 'department', label: 'واحداجرایی', wrap: true },
  { key: 'remark', label: 'عنوان', wrap: true },
  { key: 'postal_code', label: 'کدپستی' },
  { key: 'address', label: 'آدرس', wrap: true },
  { key: 'facilities_count', label: 'تعداد منصوبات', num: true },
  { key: 'bills_count', label: 'تعداد قبوض', num: true },
  { key: 'last_bill_request', label: 'آخرین درخواست قبوض' },
  { key: 'last_insure_request', label: 'آخرین درخواست بیمه' },
  { key: 'last_repair_request', label: 'آخرین درخواست تعمیرات' },
];
const FAC_COLS = [
  { key: 'facility', label: 'اشتراک' },
  { key: 'identity', label: 'شناسایی' },
  { key: 'facility_no', label: 'شماره' },
  { key: 'remark', label: 'توضیحات', wrap: true },
  { key: 'bill_count', label: 'تعداد قبض', num: true },
  { key: 'last_bill_date', label: 'تاریخ آخرین قبض' },
  { key: 'user_group', label: 'گروه کاربری' },
];

const EMPTY = { city_id: 0, city: '', department_id: 0, department: '', remark: '', postal_code: '', address: '' };

function GroupBtns({ title, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '4px 8px' }}>
      <span style={ui.flabel}>{title}</span>
      <div style={{ display: 'flex', gap: '6px' }}>{children}</div>
    </div>
  );
}

const smallBtn = { ...ui.btnItems, padding: '6px 10px', fontSize: '12px', cursor: 'pointer' };

export default function Buildings({ user, onBack }) {
  const fb = useFormButtons(FORM, BUTTONS);
  const [year, setYear] = useState(curYear());
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [modal, setModal] = useState(null);          // new | edit
  const [form, setForm] = useState(EMPTY);
  const [formErr, setFormErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);              // پیام یک‌دکمه‌ای
  const [ask, setAsk] = useState(null);              // {lines, onYes}
  const [choice, setChoice] = useState(null);        // انتخاب تاییدیه / صورتجلسه
  const [uses, setUses] = useState(null);
  const [sub, setSub] = useState(null);              // facilities | insure | repair

  const L = usePagedList((page, o) =>
    API(`/?page=${page}&year=${o.year || ''}&search=${encodeURIComponent(o.search || '')}`));

  const reload = useCallback((keepId) => {
    L.reload({ year, search }).then(j => {
      if (!j || !j.ok) return;
      const rows = j.data || [];
      const hit = keepId ? rows.find(r => r.id === keepId) : null;
      setSelected(hit || rows[0] || null);
    });
  }, [L, year, search]);

  useEffect(() => { if (fb.loaded && fb.open) reload(); }, [fb.loaded, fb.open]);

  useEffect(() => {
    if (!selected) { setFacilities([]); return; }
    API(`/${selected.id}/facilities/`).then(j => setFacilities(j.ok ? j.data : []));
  }, [selected]);

  const showMsg = m => setMsg(splitLines(m));
  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const canAct = fb.open;

  const openNew = () => { setForm(EMPTY); setFormErr(''); setModal('new'); };
  const openEdit = () => {
    if (!selected) return;
    setForm({ city_id: selected.city_id, city: selected.city, department_id: selected.department_id,
      department: selected.department, remark: selected.remark, postal_code: selected.postal_code, address: selected.address });
    setFormErr(''); setModal('edit');
  };

  const save = async () => {
    setSaving(true); setFormErr('');
    const body = JSON.stringify(form);
    const j = modal === 'new'
      ? await API('/create/', { method: 'POST', body })
      : await API(`/${selected.id}/update/`, { method: 'PUT', body });
    setSaving(false);
    if (j.ok) { setModal(null); reload(j.id); } else setFormErr(j.msg);
  };

  const doDelete = async () => {
    setAsk(null);
    const j = await API(`/${selected.id}/delete/`, { method: 'DELETE' });
    if (j.ok) { setSelected(null); reload(); } else showMsg(j.msg);
  };
  const askDelete = () => {
    if (!selected) return;
    setAsk({ title: 'هشدار', lines: ['آيا از حذف اين رکورد مطمئن هستيد؟'], onYes: doDelete });
  };

  const openSub = kind => {
    if (!selected) return;
    if (String(selected.postal_code || '').trim().length !== 10) { showMsg('کد پستی درست ثبت نشده است'); return; }
    setSub(kind);
  };

  const confirmReport = key => setChoice({ key });

  const openUses = async () => {
    if (!selected) return;
    const j = await API(`/${selected.id}/uses/?year=${year}`);
    if (!j.ok) { showMsg(j.msg); return; }
    if (!j.cnt) { showMsg(j.msg); return; }
    setUses(j);
  };
  const doMove = async (targetId, thisYear) => {
    const j = await API(`/${selected.id}/move/`, { method: 'POST',
      body: JSON.stringify({ target_id: targetId, this_year: thisYear ? 1 : 0, year }) });
    if (!j.ok) { showMsg(j.msg); return; }
    setUses(null);
    setAsk({ title: 'انتخاب کنید', lines: ['تغییر انجام شد', '', '', 'آیا رکورد فعلی حذف شود؟'], onYes: () => { setAsk(null); askDelete(); } });
    reload(selected.id);
  };

  const closeSub = () => { setSub(null); reload(selected && selected.id); };

  const shown = visibleCols(COLS, L.data);
  const facShown = visibleCols(FAC_COLS, facilities);
  const has = !!selected;

  const toolbar = (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <span style={ui.flabel}>سال مالی</span>
        <input value={year} onChange={e => setYear(Number(e.target.value) || '')} style={ui.yearInput}
          onKeyDown={e => e.key === 'Enter' && reload()} />
      </div>
      {canAct && (
        <div style={ui.toolBtns}>
          {fb.can('BTNNEW') && <button onClick={openNew} style={{ ...ui.btnNew, cursor: 'pointer' }}>＋ جدید</button>}
          {fb.can('BTNEDIT') && L.data.length > 0 && <button onClick={openEdit} disabled={!has} style={{ ...ui.btnEdit, opacity: has ? 1 : 0.4, cursor: 'pointer' }}>✎ ویرایش</button>}
          {fb.can('BTNDELETE') && L.data.length > 0 && <button onClick={askDelete} disabled={!has} style={{ ...ui.btnDel, opacity: has ? 1 : 0.4, cursor: 'pointer' }}>✕ حذف</button>}
          {fb.can('BTNCHANGE') && L.data.length > 0 && <button onClick={openUses} disabled={!has} style={{ ...ui.btnItems, cursor: 'pointer' }}>تغییر موارد استفاده</button>}
        </div>
      )}
      {canAct && (fb.can('BTNBUILDINGFACILITIES') || fb.can('BTNBUILDINGFACILITIESREQUEST') || fb.can('BTNBUILDINGFACILITIESCONFIRM')) && (
        <GroupBtns title="منصوبات">
          {fb.can('BTNBUILDINGFACILITIES') && L.data.length > 0 && <button onClick={() => openSub('facilities')} disabled={!has} style={smallBtn}>منصوبات</button>}
          {fb.can('BTNBUILDINGFACILITIESREQUEST') && <button onClick={() => runReport('tadarokat.building_facilities_request', {}, showMsg)} style={smallBtn}>برگه درخواست</button>}
          {fb.can('BTNBUILDINGFACILITIESCONFIRM') && <button onClick={() => confirmReport('tadarokat.building_facilities_confirm')} style={smallBtn}>تاییدیه</button>}
        </GroupBtns>
      )}
      {canAct && (fb.can('BTNBUILDINGINSURE') || fb.can('BTNINSUREREQUEST') || fb.can('BTNINSURECONFIRM')) && (
        <GroupBtns title="بیمه نامه">
          {fb.can('BTNBUILDINGINSURE') && <button onClick={() => openSub('insure')} disabled={!has} style={smallBtn}>ثبت بیمه نامه</button>}
          {fb.can('BTNINSUREREQUEST') && <button onClick={() => runReport('tadarokat.building_insure_request', {}, showMsg)} style={smallBtn}>برگه درخواست</button>}
          {fb.can('BTNINSURECONFIRM') && <button onClick={() => confirmReport('tadarokat.building_insure_confirm')} style={smallBtn}>تاییدیه</button>}
        </GroupBtns>
      )}
      {canAct && (fb.can('BTNBUILDINGREPAIR') || fb.can('BTNREPAIRREQUEST') || fb.can('BTNREPAIRCONFIRM')) && (
        <GroupBtns title="هزینه های تعمیر و نگهداری">
          {fb.can('BTNBUILDINGREPAIR') && <button onClick={() => openSub('repair')} disabled={!has} style={smallBtn}>ثبت فاکتور</button>}
          {fb.can('BTNREPAIRREQUEST') && <button onClick={() => runReport('tadarokat.building_repair_request', {}, showMsg)} style={smallBtn}>برگه درخواست</button>}
          {fb.can('BTNREPAIRCONFIRM') && <button onClick={() => confirmReport('tadarokat.building_repair_confirm')} style={smallBtn}>تاییدیه</button>}
        </GroupBtns>
      )}
      <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && reload()}
        style={{ ...ui.searchInput, flex: 1, minWidth: '160px' }} placeholder="جستجو" />
      <CountBadge shown={L.data.length} total={L.pager.total} loading={L.loading} unit="ساختمان" />
      <ExcelButton title="ساختمانها" subtitle={`سال مالی ${toFa(year)}`} columns={shown} rows={L.data} />
    </>
  );

  return (
    <>
      <ListPage user={user} onBack={onBack} title="ساختمانها" subtitle="تدارکات — سامانه ارجان"
        toolbar={toolbar} error={L.error} pager={L.pager} onMore={L.more} loading={L.loading} unit="ساختمان">
        <div style={ui.splitWrap}>
          <div style={ui.topPane}>
            <Grid columns={shown} rows={L.data} selectedId={selected && selected.id} loading={L.loading}
              onSelect={setSelected} empty="ساختمانی یافت نشد" />
          </div>
          <div style={ui.bottomPane}>
            {facilities.length > 0 && <Grid columns={facShown} rows={facilities} zebra="#fffde7" />}
          </div>
        </div>
      </ListPage>

      {(modal === 'new' || modal === 'edit') && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '680px' }} onClick={e => e.stopPropagation()}>
            <div style={ui.mHeader}>
              <div style={ui.mTitle}>{modal === 'new' ? 'ساختمان جدید' : 'ویرایش ساختمان'}</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={{ ...ui.mBody, overflow: 'visible' }}>
              {formErr && <div style={ui.errBox}>⚠️ {formErr}</div>}
              <div style={ui.row}>
                <Lookup label="شهر" value={form.city} endpoint="/lookup/cities/"
                  onPick={r => setForm(f => ({ ...f, city_id: r.id, city: r.title }))} />
                <Lookup label="واحد اجرایی" value={form.department} endpoint="/lookup/departments/"
                  onPick={r => setForm(f => ({ ...f, department_id: r.id, department: r.title }))} />
              </div>
              <div style={ui.row}>
                <Field label="عنوان" value={form.remark} onChange={v => F('remark', v)} flex={2} />
                <Field label="کدپستی" value={form.postal_code} onChange={v => F('postal_code', v)} ltr />
              </div>
              <div style={ui.row}>
                <Field label="آدرس" value={form.address} onChange={v => F('address', v)} />
              </div>
            </div>
            <div style={ui.mFooter}>
              {fb.can('BTNCANCEL') && <button onClick={() => setModal(null)} style={ui.btnCancel}>لغو</button>}
              {fb.can('BTNOK') && <button onClick={save} disabled={saving} style={ui.btnSave}>{saving ? '...' : 'ثبت'}</button>}
            </div>
          </div>
        </div>
      )}

      {uses && <ChangeUsesDialog info={uses} targetsEndpoint={`/${selected.id}/targets/`}
        notChosenMsg="ساختمان جایگزین انتخاب نشده است" onMove={doMove} onClose={() => setUses(null)} />}

      {choice && <ChoiceBox options={['صورتجلسه مصرف', 'تاییدیه']} onCancel={() => setChoice(null)}
        onPick={o => { const k = choice.key; setChoice(null); runReport(k, { is_confirm: o === 'تاییدیه' ? 1 : 0 }, showMsg); }} />}

      {ask && <WarningDialog title={ask.title} lines={ask.lines} confirmLabel="بلی" cancelLabel="خیر"
        onConfirm={ask.onYes} onCancel={() => setAsk(null)} tone="danger" />}
      {msg && <MessageBox lines={msg} onClose={() => setMsg(null)} />}

      {sub === 'facilities' && selected && (
        <SubFormShell>
          <BuildingFacilities user={user} onBack={closeSub} building={selected} year={year} />
        </SubFormShell>
      )}
      {sub === 'insure' && selected && (
        <SubFormShell>
          <BuildingInsure user={user} onBack={closeSub} building={selected} year={year} />
        </SubFormShell>
      )}
      {sub === 'repair' && selected && (
        <SubFormShell>
          <BuildingRepair user={user} onBack={closeSub} building={selected} year={year} />
        </SubFormShell>
      )}
    </>
  );
}
